/**
 * HeaderConfigPanel Component for UI-Model-1
 *
 * Full header configuration form using Design System components.
 * Integrates with the command pattern for undo/redo support.
 */

import React, { useCallback, useRef } from 'react';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { createUpdateHeaderCommand } from '../../types/command.types';
import { HeaderConfigurationAPI } from '../../types/api.types';
import { SaveDataByType } from '../../services/SaveQueue';
import { IconUploadSection } from './shared/IconUploadSection';
import { ButtonConfigSection } from './shared/ButtonConfigSection';

interface HeaderConfigPanelProps {
  id?: string;
}

// Title alignment options for dropdown (matching old UI-Model)
const TITLE_ALIGNMENT_OPTIONS = [
  { value: 'Left', label: 'Left, Below Logo' },
  { value: 'Centre', label: 'Centre' },
];

/**
 * Main HeaderConfigPanel Component
 */
export const HeaderConfigPanel = ({ id }: HeaderConfigPanelProps) => {
  const baseId = id || 'header-config-panel';
  const { state, dispatch } = useUIModelState();
  const { enqueueSave } = useSaveQueue();
  const headerConfig = state.model?.layout.header?.configuration;

  // Refs for file uploads (to track pending files for save)
  const logoFileRef = useRef<File | undefined>(undefined);
  const resetButtonIconFileRef = useRef<File | undefined>(undefined);
  const closeButtonIconFileRef = useRef<File | undefined>(undefined);
  const saveButtonIconFileRef = useRef<File | undefined>(undefined);

  // Helper to dispatch header updates
  const updateHeader = useCallback(
    (updates: Partial<HeaderConfigurationAPI>, description?: string) => {
      const command = createUpdateHeaderCommand(
        updates,
        description || 'Update header configuration'
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'header' } });

      // Merge updates with current header to avoid stale closure issue
      if (headerConfig) {
        const updatedHeader = {
          ...headerConfig,
          ...updates,
        };
        const saveData: SaveDataByType['header'] = {
          headerData: updatedHeader,
          logoFile: logoFileRef.current,
          resetButtonIconFile: resetButtonIconFileRef.current,
          closeButtonIconFile: closeButtonIconFileRef.current,
          saveButtonIconFile: saveButtonIconFileRef.current,
        };
        enqueueSave('header', saveData);
      }
    },
    [dispatch, headerConfig, enqueueSave]
  );

  // Debounced inputs for main text fields
  const titleTextInput = useDebouncedInput(
    headerConfig?.titleText || '',
    (value) => updateHeader({ titleText: value }, 'Update header title'),
    2000
  );

  if (!headerConfig) {
    return (
      <div className="tw-p-4 tw-text-ink-tertiary tw-text-sm">
        No header configuration available
      </div>
    );
  }

  const isDisabled = !headerConfig.showHeader;

  return (
    <div id={baseId} data-testid={baseId} className="tw-space-y-6">
      {/* Show Header Toggle - at top of panel */}
      {/* Role tokens (2026-09-09, user screenshot): `grey-900` title was navy on the dark
          ladder (invisible) and the `grey-200` rule a bright white line — literals can only
          be right in one theme. `ink` / `edge` follow data-theme. */}
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-4 tw-border-b tw-border-edge">
        <span className="tw-text-title tw-text-ink">Header</span>
        <Toggle
          label=""
          checked={headerConfig.showHeader}
          onChange={(checked) =>
            updateHeader({ showHeader: checked }, `${checked ? 'Show' : 'Hide'} header`)
          }
        />
      </div>

      {/* Section: Logo */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50 tw-pointer-events-none' : ''}`}>
        <IconUploadSection
          label="Logo"
          currentUrl={headerConfig.logoUrl}
          onUrlChange={(url) => {
            if (!url) logoFileRef.current = undefined;
            updateHeader({ logoUrl: url }, 'Update header logo');
          }}
          onFileChange={(file) => {
            logoFileRef.current = file;
          }}
          inputId={`${baseId}-logo`}
          disabled={isDisabled}
        />
      </div>

      {/* Section: Title */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50 tw-pointer-events-none' : ''}`}>
        <Checkbox
          label="Show 'Title'"
          checked={headerConfig.showTitle}
          onChange={(checked) =>
            updateHeader({ showTitle: checked }, `${checked ? 'Show' : 'Hide'} title`)
          }
          disabled={isDisabled}
        />

        {headerConfig.showTitle && (
          <div className="tw-space-y-3 tw-pl-4 tw-border-l-2 tw-border-edge">
            <TextField
              label="Change Title Name"
              labelSize="sm"
              value={titleTextInput.localValue}
              onChange={titleTextInput.handleChange}
              placeholder="Enter header title"
              disabled={isDisabled}
            />

            <Dropdown
              label="Title Layout"
              labelSize="sm"
              options={TITLE_ALIGNMENT_OPTIONS}
              value={headerConfig.titleLayout}
              onChange={(value) =>
                updateHeader(
                  { titleLayout: value as HeaderConfigurationAPI['titleLayout'] },
                  `Set title alignment to ${value}`
                )
              }
              disabled={isDisabled}
            />
          </div>
        )}
      </div>

      {/* Section: Your Build */}
      <div id={`${baseId}-your-build-section`} className={`tw-space-y-3 ${isDisabled ? 'tw-opacity-50 tw-pointer-events-none' : ''}`}>
        <Checkbox
          label="Show 'Your Build'"
          checked={headerConfig.showYourBuild ?? false}
          onChange={(checked) =>
            updateHeader({ showYourBuild: checked }, `${checked ? 'Show' : 'Hide'} Your Build`)
          }
          disabled={isDisabled}
        />

        {(headerConfig.showYourBuild ?? false) && (
          <div className="tw-pl-4 tw-border-l-2 tw-border-edge">
            <button
              id={`${baseId}-your-build-configure-btn`}
              data-testid={`${baseId}-your-build-configure-btn`}
              onClick={() =>
                dispatch({
                  type: 'SET_SELECTED_SECTION',
                  payload: { section: 'header-your-build' },
                })
              }
              /* Role tokens (2026-09-09): `#091CAA` is accent-bold's LIGHT value — a navy
                 literal on the dark ladder, where accent resolves to the brand cyan. The
                 white/grey-200/grey-100 chrome themed the same way. */
              className="tw-w-full tw-flex tw-items-center tw-justify-between tw-border tw-border-edge tw-rounded-ctl tw-px-3 tw-py-2 tw-bg-surface hover:tw-bg-surface-hover tw-transition-colors"
            >
              <span className="tw-text-sm tw-text-accent">Configure</span>
              <svg className="tw-w-4 tw-h-4 tw-text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Section: Reset Button */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50 tw-pointer-events-none' : ''}`}>
        <FieldTitle label="Reset Button" size="sm" />
        <ButtonConfigSection
          buttonName="Reset"
          showButton={headerConfig.showResetButton}
          buttonText={headerConfig.resetButtonText}
          showIcon={headerConfig.resetButtonShowIcon}
          iconUrl={headerConfig.resetButtonIconUrl}
          showTooltip={headerConfig.resetButtonShowTooltip}
          tooltipText={headerConfig.resetButtonTooltipText}
          onShowButtonChange={(show) =>
            updateHeader({ showResetButton: show }, `${show ? 'Show' : 'Hide'} reset button`)
          }
          onTextChange={(text) =>
            updateHeader({ resetButtonText: text }, 'Update reset button text')
          }
          onShowIconChange={(show) =>
            updateHeader({ resetButtonShowIcon: show }, `${show ? 'Show' : 'Hide'} reset button icon`)
          }
          onIconUrlChange={(url) => {
            if (!url) resetButtonIconFileRef.current = undefined;
            updateHeader({ resetButtonIconUrl: url }, 'Update reset button icon');
          }}
          onIconFileChange={(file) => {
            resetButtonIconFileRef.current = file;
          }}
          onShowTooltipChange={(show) =>
            updateHeader({ resetButtonShowTooltip: show }, `${show ? 'Show' : 'Hide'} reset button tooltip`)
          }
          onTooltipTextChange={(text) =>
            updateHeader({ resetButtonTooltipText: text }, 'Update reset button tooltip')
          }
          baseId={baseId}
          disabled={isDisabled}
        />
      </div>

      {/* Section: Close Button */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50 tw-pointer-events-none' : ''}`}>
        <FieldTitle label="Close Button" size="sm" />
        <ButtonConfigSection
          buttonName="Close"
          showButton={headerConfig.showCloseButton}
          buttonText={headerConfig.closeButtonText}
          showIcon={headerConfig.closeButtonShowIcon}
          iconUrl={headerConfig.closeButtonIconUrl}
          showTooltip={headerConfig.closeButtonShowTooltip}
          tooltipText={headerConfig.closeButtonTooltipText}
          onShowButtonChange={(show) =>
            updateHeader({ showCloseButton: show }, `${show ? 'Show' : 'Hide'} close button`)
          }
          onTextChange={(text) =>
            updateHeader({ closeButtonText: text }, 'Update close button text')
          }
          onShowIconChange={(show) =>
            updateHeader({ closeButtonShowIcon: show }, `${show ? 'Show' : 'Hide'} close button icon`)
          }
          onIconUrlChange={(url) => {
            if (!url) closeButtonIconFileRef.current = undefined;
            updateHeader({ closeButtonIconUrl: url }, 'Update close button icon');
          }}
          onIconFileChange={(file) => {
            closeButtonIconFileRef.current = file;
          }}
          onShowTooltipChange={(show) =>
            updateHeader({ closeButtonShowTooltip: show }, `${show ? 'Show' : 'Hide'} close button tooltip`)
          }
          onTooltipTextChange={(text) =>
            updateHeader({ closeButtonTooltipText: text }, 'Update close button tooltip')
          }
          baseId={baseId}
          disabled={isDisabled}
        />
      </div>

      {/* Section: Save Button */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50 tw-pointer-events-none' : ''}`}>
        <FieldTitle label="Save Button" size="sm" />
        <ButtonConfigSection
          buttonName="Save"
          showButton={headerConfig.showSaveButton ?? false}
          buttonText={headerConfig.saveButtonText ?? 'Save'}
          showIcon={headerConfig.saveButtonShowIcon ?? false}
          iconUrl={headerConfig.saveButtonIconUrl}
          showTooltip={headerConfig.saveButtonShowTooltip ?? false}
          tooltipText={headerConfig.saveButtonTooltipText}
          onShowButtonChange={(show) =>
            updateHeader({ showSaveButton: show }, `${show ? 'Show' : 'Hide'} save button`)
          }
          onTextChange={(text) =>
            updateHeader({ saveButtonText: text }, 'Update save button text')
          }
          onShowIconChange={(show) =>
            updateHeader({ saveButtonShowIcon: show }, `${show ? 'Show' : 'Hide'} save button icon`)
          }
          onIconUrlChange={(url) => {
            if (!url) saveButtonIconFileRef.current = undefined;
            updateHeader({ saveButtonIconUrl: url }, 'Update save button icon');
          }}
          onIconFileChange={(file) => {
            saveButtonIconFileRef.current = file;
          }}
          onShowTooltipChange={(show) =>
            updateHeader({ saveButtonShowTooltip: show }, `${show ? 'Show' : 'Hide'} save button tooltip`)
          }
          onTooltipTextChange={(text) =>
            updateHeader({ saveButtonTooltipText: text }, 'Update save button tooltip')
          }
          baseId={baseId}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};

export default HeaderConfigPanel;
